import { useState, useEffect, useCallback, useRef } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import EmployeeClock from './pages/EmployeeClock';
import ManagerLogin from './pages/ManagerLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ManagerDashboard from './pages/ManagerDashboard';
import ManagerEmployees from './pages/ManagerEmployees';
import ManagerLocations from './pages/ManagerLocations';
import ManagerTimesheets from './pages/ManagerTimesheets';
import ManagerExceptions from './pages/ManagerExceptions';
import ManagerEditLog from './pages/ManagerEditLog';
import ManagerDataManagement from './pages/ManagerDataManagement';
import { toast } from 'sonner';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [manager, setManager] = useState(null);
  const inactivityTimerRef = useRef(null);
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

  useEffect(() => {
    // Use sessionStorage instead of localStorage for auto-logout on browser close
    const token = sessionStorage.getItem('managerToken');
    const managerData = sessionStorage.getItem('managerData');
    if (token && managerData) {
      setIsAuthenticated(true);
      setManager(JSON.parse(managerData));
    }
  }, []);

  const handleLogout = useCallback(() => {
    sessionStorage.removeItem('managerToken');
    sessionStorage.removeItem('managerData');
    setIsAuthenticated(false);
    setManager(null);
    
    // Clear inactivity timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
  }, []);

  const resetInactivityTimer = useCallback(() => {
    // Clear existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Set new timer
    if (isAuthenticated) {
      inactivityTimerRef.current = setTimeout(() => {
        toast.error('Session expired due to inactivity. Please log in again.');
        handleLogout();
      }, INACTIVITY_TIMEOUT);
    }
  }, [isAuthenticated, handleLogout, INACTIVITY_TIMEOUT]);

  // Track user activity
  useEffect(() => {
    if (isAuthenticated) {
      // Start initial timer
      resetInactivityTimer();

      // Activity event listeners
      const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
      
      activityEvents.forEach(event => {
        window.addEventListener(event, resetInactivityTimer);
      });

      // Cleanup
      return () => {
        activityEvents.forEach(event => {
          window.removeEventListener(event, resetInactivityTimer);
        });
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
      };
    }
  }, [isAuthenticated, resetInactivityTimer]);

  const handleLogin = (token, managerData) => {
    sessionStorage.setItem('managerToken', token);
    sessionStorage.setItem('managerData', JSON.stringify(managerData));
    setIsAuthenticated(true);
    setManager(managerData);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<EmployeeClock />} />
          <Route
            path="/manager/login"
            element={
              isAuthenticated ? (
                <Navigate to="/manager/dashboard" replace />
              ) : (
                <ManagerLogin onLogin={handleLogin} />
              )
            }
          />
          <Route path="/manager/forgot-password" element={<ForgotPassword />} />
          <Route path="/manager/reset-password" element={<ResetPassword />} />
          <Route
            path="/manager/dashboard"
            element={
              isAuthenticated ? (
                <ManagerDashboard manager={manager} onLogout={handleLogout} />
              ) : (
                <Navigate to="/manager/login" replace />
              )
            }
          />
          <Route
            path="/manager/employees"
            element={
              isAuthenticated ? (
                <ManagerEmployees manager={manager} onLogout={handleLogout} />
              ) : (
                <Navigate to="/manager/login" replace />
              )
            }
          />
          <Route
            path="/manager/locations"
            element={
              isAuthenticated ? (
                <ManagerLocations manager={manager} onLogout={handleLogout} />
              ) : (
                <Navigate to="/manager/login" replace />
              )
            }
          />
          <Route
            path="/manager/timesheets"
            element={
              isAuthenticated ? (
                <ManagerTimesheets manager={manager} onLogout={handleLogout} />
              ) : (
                <Navigate to="/manager/login" replace />
              )
            }
          />
          <Route
            path="/manager/exceptions"
            element={
              isAuthenticated ? (
                <ManagerExceptions manager={manager} onLogout={handleLogout} />
              ) : (
                <Navigate to="/manager/login" replace />
              )
            }
          />
          <Route
            path="/manager/edit-log"
            element={
              isAuthenticated ? (
                <ManagerEditLog manager={manager} onLogout={handleLogout} />
              ) : (
                <Navigate to="/manager/login" replace />
              )
            }
          />
          <Route
            path="/manager/data-management"
            element={
              isAuthenticated ? (
                <ManagerDataManagement manager={manager} onLogout={handleLogout} />
              ) : (
                <Navigate to="/manager/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;